import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, TrendingUp, QrCode, Copy, Download, Link as LinkIcon } from 'lucide-react';

const BASE_URL = (typeof window !== 'undefined' ? window.location.origin : '');

const Bar = ({ label, value, max, color = 'bg-primary' }) => (
  <div className="flex items-center gap-3 py-1.5" data-testid="acq-bar">
    <div className="w-40 text-sm text-gray-600 truncate flex-shrink-0">{label}</div>
    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
      <div className={`h-5 ${color} rounded-full transition-all`} style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
    </div>
    <div className="w-10 text-right text-sm font-semibold text-primary flex-shrink-0">{value}</div>
  </div>
);

const AdminMarketing = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // UTM builder
  const [utm, setUtm] = useState({ landing: '/', source: '', medium: '', campaign: '', term: '', content: '' });
  const builtUrl = (() => {
    const base = `${BASE_URL}${utm.landing.startsWith('/') ? '' : '/'}${utm.landing}`;
    const p = new URLSearchParams();
    if (utm.source) p.set('utm_source', utm.source);
    if (utm.medium) p.set('utm_medium', utm.medium);
    if (utm.campaign) p.set('utm_campaign', utm.campaign);
    if (utm.term) p.set('utm_term', utm.term);
    if (utm.content) p.set('utm_content', utm.content);
    const qs = p.toString();
    return qs ? `${base}?${qs}` : base;
  })();
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(builtUrl)}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/marketing/acquisition?days=${days}`);
      setData(res.data);
    } catch (e) { toast.error('Could not load (admin only).'); } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { document.title = 'Marketing & Acquisition | Admin'; load(); }, [load]);

  const copyUrl = () => { navigator.clipboard.writeText(builtUrl); toast.success('Tagged link copied!'); };
  const maxSource = data ? Math.max(1, ...data.by_source.map((s) => s[1])) : 1;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-sm text-gray-500 hover:text-primary flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Admin Dashboard</Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="font-serif text-3xl font-bold text-primary flex items-center gap-2" data-testid="marketing-title"><TrendingUp className="w-7 h-7 text-secondary" /> Marketing &amp; Acquisition</h1>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`range-${d}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${days === d ? 'bg-primary text-white' : 'bg-white border text-gray-600'}`}>{d}d</button>
            ))}
          </div>
        </div>

        {loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : data && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border p-6" data-testid="acq-by-source">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-bold text-primary">New members by source</h2>
                <span className="text-sm text-gray-400">{data.total} in {days}d</span>
              </div>
              {data.by_source.length === 0 ? <p className="text-sm text-gray-400">No signups in this window.</p> :
                data.by_source.map(([label, v]) => <Bar key={label} label={label} value={v} max={maxSource} />)}
            </div>

            <div className="bg-white rounded-2xl border p-6" data-testid="acq-job-driven">
              <h2 className="font-bold text-primary mb-1">Job-driven registrations</h2>
              <p className="text-3xl font-extrabold text-secondary mb-3" data-testid="job-driven-count">{data.job_driven.count}</p>
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                {[['Top jobs', data.job_driven.top_jobs], ['Top employers', data.job_driven.top_employers], ['Top states', data.job_driven.top_states]].map(([t, rows]) => (
                  <div key={t}>
                    <p className="font-semibold text-gray-700 mb-1">{t}</p>
                    {rows.length === 0 ? <p className="text-gray-400 text-xs">—</p> : rows.map(([k, v]) => (
                      <p key={k} className="text-gray-600 text-xs truncate">{k} <span className="text-gray-400">· {v}</span></p>
                    ))}
                  </div>
                ))}
              </div>
              {data.by_utm.length > 0 && (
                <div className="mt-4 pt-3 border-t">
                  <p className="font-semibold text-gray-700 text-sm mb-1">Top UTM sources</p>
                  {data.by_utm.slice(0, 5).map(([k, v]) => <span key={k} className="inline-block text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 mr-1 mb-1">{k} · {v}</span>)}
                </div>
              )}
            </div>

            {/* UTM & QR builder */}
            <div className="bg-white rounded-2xl border p-6 lg:col-span-2" data-testid="utm-builder">
              <h2 className="font-bold text-primary flex items-center gap-2 mb-4"><LinkIcon className="w-5 h-5" /> UTM &amp; QR link builder</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2 grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs text-gray-500">Landing page path</Label><Input value={utm.landing} onChange={(e) => setUtm({ ...utm, landing: e.target.value })} placeholder="/jobs" data-testid="utm-landing" /></div>
                  <div><Label className="text-xs text-gray-500">Source *</Label><Input value={utm.source} onChange={(e) => setUtm({ ...utm, source: e.target.value })} placeholder="facebook" data-testid="utm-source" /></div>
                  <div><Label className="text-xs text-gray-500">Medium</Label><Input value={utm.medium} onChange={(e) => setUtm({ ...utm, medium: e.target.value })} placeholder="flyer" data-testid="utm-medium" /></div>
                  <div><Label className="text-xs text-gray-500">Campaign</Label><Input value={utm.campaign} onChange={(e) => setUtm({ ...utm, campaign: e.target.value })} placeholder="spring_conference" data-testid="utm-campaign" /></div>
                  <div><Label className="text-xs text-gray-500">Term</Label><Input value={utm.term} onChange={(e) => setUtm({ ...utm, term: e.target.value })} placeholder="lcsw" data-testid="utm-term" /></div>
                  <div><Label className="text-xs text-gray-500">Content</Label><Input value={utm.content} onChange={(e) => setUtm({ ...utm, content: e.target.value })} placeholder="poster_a" data-testid="utm-content" /></div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-gray-500">Tagged link</Label>
                    <div className="flex gap-2 mt-1">
                      <Input readOnly value={builtUrl} data-testid="utm-result" className="font-mono text-xs bg-gray-50" />
                      <Button type="button" onClick={copyUrl} data-testid="utm-copy" className="bg-primary text-white flex-shrink-0"><Copy className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <Label className="text-xs text-gray-500 block mb-2">QR code</Label>
                  <img src={qrUrl} alt="Campaign QR code" className="mx-auto rounded-lg border" width={200} height={200} data-testid="utm-qr" />
                  <a href={qrUrl} download="inasw-campaign-qr.png" target="_blank" rel="noreferrer">
                    <Button type="button" variant="outline" className="mt-3" data-testid="utm-qr-download"><Download className="w-4 h-4 mr-1" /> Download QR</Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketing;
